import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { pathOr, prop, without } from 'ramda';
import { useComponentConfig } from '@namespace/cms';
import { useHistory } from '@namespace/router';
import {
  useCategoriesBySportId,
  useSportsbookStore,
  useTournamentsByIds,
  useTournamentsIds
} from '../../store';
import { MenuListSkelet } from '../MenuListSkelet';
import { SportItem } from '../SportItem';
import { SubMenuListSkelet } from '../SubMenuListSkelet';
import { ExpandedCategory } from './ExpandedCategory';
import { createCategoryItemPath } from './utils';

import styles from './index.module.css';

export const FilterMenuList = memo((props) => {
  const {
    serviceId,
    activeSportId,
    activeCategoryId,
    tournamentIds,
    baseSportPath,
    baseCategoryPath,
    baseTournamentPath,
    onChange
  } = props;
  const config = useComponentConfig();
  const sports = useSportsbookStore(pathOr([], ['sports', serviceId]));
  const favoritesSports = useSportsbookStore(
    pathOr([], ['favorites', 'sports'])
  );
  const categoriesBySportId = useCategoriesBySportId(activeSportId, serviceId);
  const allTournamentsIds = useTournamentsIds(serviceId);
  const { push } = useHistory();

  // validated tournamentIds
  const activeTournamentsIds = useMemo(
    () =>
      allTournamentsIds.filter((tournamentId) =>
        tournamentIds.includes(tournamentId)
      ),
    [tournamentIds, allTournamentsIds]
  );
  // validated tournaments
  const activeTournaments = useTournamentsByIds(
    activeTournamentsIds,
    serviceId
  );

  const {
    showCategories = true,
    showTournaments = true,
    isSingleTournament = false,
    isShowEventsCount = false
  } = config;
  const isSportExpandable = showCategories || showTournaments;
  const isCategoryExpandable = showTournaments;

  const openedCategoryId =
    activeCategoryId ?? activeTournaments.map(prop('categoryId'))[0];

  const [expandedSportId, setExpandedSportId] = useState(activeSportId);
  const [expandedCategoryId, setExpandedCategoryId] =
    useState(openedCategoryId);

  const onToggleSport = useCallback(
    (sportId) => {
      if (sportId === expandedSportId) {
        setExpandedSportId(null);
      } else {
        setExpandedSportId(sportId);
      }
      setExpandedCategoryId(null);
    },
    [expandedSportId, setExpandedSportId]
  );

  const onToggleCategory = useCallback(
    (categoryId) => {
      if (categoryId === expandedCategoryId) {
        setExpandedCategoryId(null);
      } else {
        setExpandedCategoryId(categoryId);
      }
    },
    [expandedCategoryId]
  );

  // Collapse category and preserve timeFilter. If tournaments were selected - navigate to a category and reset timeFilter.
  const onCategoryExpandClick = useCallback(
    (categoryId) => {
      onToggleCategory(categoryId);

      const path = createCategoryItemPath({
        basePath: baseCategoryPath,
        categoryId,
        isActive: tournamentIds.length === 0,
        search: window.location.search
      });

      push(path);
    },
    [baseCategoryPath, onToggleCategory, push, tournamentIds.length]
  );

  const onTournamentClick = useCallback(
    (newTournamentId, newCategoryId) => {
      let nextTournamentIds;
      let nextCategoryId = null;

      if (isSingleTournament) {
        nextTournamentIds = [newTournamentId];
      } else if (tournamentIds.includes(newTournamentId)) {
        nextTournamentIds = without([newTournamentId], tournamentIds);
        if (nextTournamentIds.length === 0) {
          nextCategoryId = newCategoryId;
        }
      } else {
        nextTournamentIds = [...tournamentIds, newTournamentId];
      }

      // this handler is only needed for tournaments as categories and sports are handled by direct links
      onChange?.({
        categoryId: nextCategoryId,
        tournaments: nextTournamentIds
      });
    },
    [isSingleTournament, onChange, tournamentIds]
  );

  // ensure active sport is always expanded when selected
  useEffect(() => {
    setExpandedSportId(activeSportId);
  }, [activeSportId]);

  // ensure active category is always expanded when selected
  useEffect(() => {
    if (openedCategoryId) {
      setExpandedSportId(activeSportId);
      setExpandedCategoryId(openedCategoryId);
    }
  }, [activeSportId, openedCategoryId]);

  // ensure selected tournaments are not inside collapsed category - may happen during back navigation
  useEffect(() => {
    if (activeTournaments.length > 0) {
      const categoryIdToExpand = activeTournaments[0].categoryId;
      if (categoryIdToExpand !== expandedCategoryId) {
        setExpandedCategoryId(categoryIdToExpand);
      }
    }
  }, [activeTournaments, expandedCategoryId]);

  const preparedSports = useMemo(
    () =>
      favoritesSports.length > 0
        ? sports
            .map((sport) => ({
              ...sport,
              isPinned: favoritesSports.includes(sport.sportId)
            }))
            .sort((a, b) => b.isPinned - a.isPinned)
        : sports,
    [sports, favoritesSports]
  );

  if (preparedSports.length === 0) {
    return <MenuListSkelet />;
  }

  return preparedSports.map((sport) => {
    const { sportId } = sport;
    const isActiveSport = sportId === activeSportId;
    const isExpandedSport = sportId === expandedSportId;

    return (
      <Fragment key={`sportItem-${sportId}`}>
        <SportItem
          sport={sport}
          basePath={baseSportPath}
          isActive={isActiveSport}
          isExpanded={isExpandedSport}
          onToggle={onToggleSport}
          withCounter={!isActiveSport || !isSportExpandable}
          withExpandIcon={isActiveSport && isSportExpandable}
          showPinIcon={true}
        />
        {isSportExpandable && isActiveSport && isExpandedSport && (
          <div
            className={styles.wrapper}
            data-role={`categories-list-${sportId}`}
          >
            {categoriesBySportId.length > 0 ? (
              categoriesBySportId.map(({ categoryId }) => {
                const isExpandedCategory =
                  categoryId === expandedCategoryId || !showCategories;
                const isActiveCategory =
                  categoryId === activeCategoryId || isExpandedCategory;

                return (
                  <ExpandedCategory
                    key={categoryId}
                    serviceId={serviceId}
                    categoryId={categoryId}
                    selectedTournamentsIds={tournamentIds}
                    showCategories={showCategories}
                    isActiveCategory={isActiveCategory}
                    isCategoryExpandable={isCategoryExpandable}
                    isExpandedCategory={isExpandedCategory}
                    onToggleCategory={onToggleCategory}
                    onCategoryExpandClick={onCategoryExpandClick}
                    onTournamentClick={onTournamentClick}
                    isSingleTournament={isSingleTournament}
                    isShowEventsCount={isShowEventsCount}
                    baseCategoryPath={baseCategoryPath}
                    baseTournamentPath={baseTournamentPath}
                  />
                );
              })
            ) : (
              <SubMenuListSkelet />
            )}
          </div>
        )}
      </Fragment>
    );
  });
});

FilterMenuList.displayName = 'FilterMenuList';
